export const OPENAI_MARKER_START = '\uE200'
export const OPENAI_MARKER_DELIMITER = '\uE202'
export const OPENAI_MARKER_STOP = '\uE201'
export const OPENAI_SOURCE_ID_RE = /^[A-Za-z0-9_-]+$/

const MODEL_MARKER_PATTERN = new RegExp(
  `${OPENAI_MARKER_START}(?:cite|diagram|keyphrase|table)${OPENAI_MARKER_DELIMITER}[\\s\\S]*?${OPENAI_MARKER_STOP}`,
  'g',
)
const LEGACY_CITATION_PATTERN =
  /\s*\[\[\d+\]\]|\s*\[\s*\d+(?:\s*,\s*\d+)*\s*\]/g

export type ParsedCitationMarker = {
  sourceIds: Array<string>
  start: number
  end: number
}

export type ParsedKeyPhraseMarker = {
  text: string
  start: number
  end: number
  markerIndex: number
}

export type ParsedDiagramMarker = {
  sourceBlockId: string
  diagramReference: string
  subfigureReference: string | null
  start: number
  end: number
  markerIndex: number
}

export type ParsedTableMarker = {
  sourceBlockId: string
  tableReference: string
  start: number
  end: number
  markerIndex: number
}

function normalizeDiagramReference(value: string | null | undefined) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function normalizeSubfigureReference(value: string | null | undefined) {
  const trimmed = value?.trim()
  return trimmed && /^[a-z]$/i.test(trimmed) ? trimmed.toLowerCase() : null
}

function normalizeTableReference(value: string | null | undefined) {
  const trimmed = value?.trim().toUpperCase()
  return trimmed ? trimmed : null
}

export function stripCitationMarkers(text: string) {
  return text
    .replace(new RegExp(MODEL_MARKER_PATTERN.source, 'g'), '')
    .replace(new RegExp(LEGACY_CITATION_PATTERN.source, 'g'), '')
}

export function stripCitationMarkersWithMap(text: string) {
  const modelMarkerPattern = new RegExp(MODEL_MARKER_PATTERN.source, 'g')
  const legacyCitationPattern = new RegExp(LEGACY_CITATION_PATTERN.source, 'g')
  const rawToCleanIndex = new Array<number>(text.length + 1)
  let cleanedText = ''
  let rawIndex = 0

  while (rawIndex < text.length) {
    rawToCleanIndex[rawIndex] = cleanedText.length

    modelMarkerPattern.lastIndex = rawIndex
    const modelMatch = modelMarkerPattern.exec(text)
    if (modelMatch && modelMatch.index === rawIndex) {
      for (let i = rawIndex; i < rawIndex + modelMatch[0].length; i += 1) {
        rawToCleanIndex[i] = cleanedText.length
      }
      rawIndex += modelMatch[0].length
      continue
    }

    legacyCitationPattern.lastIndex = rawIndex
    const legacyMatch = legacyCitationPattern.exec(text)
    if (legacyMatch && legacyMatch.index === rawIndex) {
      for (let i = rawIndex; i < rawIndex + legacyMatch[0].length; i += 1) {
        rawToCleanIndex[i] = cleanedText.length
      }
      rawIndex += legacyMatch[0].length
      continue
    }

    cleanedText += text[rawIndex]
    rawIndex += 1
  }

  rawToCleanIndex[text.length] = cleanedText.length

  return {
    cleanedText,
    rawToCleanIndex,
  }
}

export function extractCitationMarkers(text: string) {
  const tokenRe = new RegExp(
    `${OPENAI_MARKER_START}cite${OPENAI_MARKER_DELIMITER}(?<body>[\\s\\S]*?)${OPENAI_MARKER_STOP}`,
    'g',
  )

  const citations: Array<ParsedCitationMarker> = []

  for (const match of text.matchAll(tokenRe)) {
    const body = match.groups?.body ?? ''
    const sourceIds = body
      .split(OPENAI_MARKER_DELIMITER)
      .map((part) => part.trim())
      .filter(Boolean)
      .filter((part) => OPENAI_SOURCE_ID_RE.test(part))

    if (!sourceIds.length) continue

    citations.push({
      sourceIds,
      start: match.index,
      end: match.index + match[0].length,
    })
  }

  return citations
}

export function extractKeyPhraseMarkers(text: string) {
  const tokenRe = new RegExp(
    `${OPENAI_MARKER_START}keyphrase${OPENAI_MARKER_DELIMITER}(?<body>[\\s\\S]*?)${OPENAI_MARKER_STOP}`,
    'g',
  )

  const keyPhrases: Array<ParsedKeyPhraseMarker> = []
  let markerIndex = 0

  for (const match of text.matchAll(tokenRe)) {
    const body = (match.groups?.body ?? '').trim()
    if (!body) continue

    keyPhrases.push({
      text: body,
      start: match.index,
      end: match.index + match[0].length,
      markerIndex,
    })
    markerIndex += 1
  }

  return keyPhrases
}

export function extractDiagramMarkers(text: string) {
  const tokenRe = new RegExp(
    `${OPENAI_MARKER_START}diagram${OPENAI_MARKER_DELIMITER}(?<body>[\\s\\S]*?)${OPENAI_MARKER_STOP}`,
    'g',
  )

  const markers: Array<ParsedDiagramMarker> = []
  let markerIndex = 0

  for (const match of text.matchAll(tokenRe)) {
    const body = match.groups?.body ?? ''
    const [sourceBlockIdRaw, diagramReferenceRaw, subfigureRaw] = body
      .split('|')
      .map((part) => part.trim())
    const sourceBlockId = sourceBlockIdRaw.trim()
    const diagramReference = normalizeDiagramReference(diagramReferenceRaw)
    const subfigureReference =
      subfigureRaw === '-' ? null : normalizeSubfigureReference(subfigureRaw)
    if (
      !sourceBlockId ||
      !OPENAI_SOURCE_ID_RE.test(sourceBlockId) ||
      !diagramReference
    ) {
      continue
    }

    markers.push({
      sourceBlockId,
      diagramReference,
      subfigureReference,
      start: match.index,
      end: match.index + match[0].length,
      markerIndex,
    })
    markerIndex += 1
  }

  return markers
}

export function extractTableMarkers(text: string) {
  const tokenRe = new RegExp(
    `${OPENAI_MARKER_START}table${OPENAI_MARKER_DELIMITER}(?<body>[\\s\\S]*?)${OPENAI_MARKER_STOP}`,
    'g',
  )

  const markers: Array<ParsedTableMarker> = []
  let markerIndex = 0

  for (const match of text.matchAll(tokenRe)) {
    const body = match.groups?.body ?? ''
    const [sourceBlockIdRaw, tableReferenceRaw] = body
      .split('|')
      .map((part) => part.trim())
    const sourceBlockId = sourceBlockIdRaw.trim()
    const tableReference = normalizeTableReference(tableReferenceRaw)
    if (
      !sourceBlockId ||
      !OPENAI_SOURCE_ID_RE.test(sourceBlockId) ||
      !tableReference
    ) {
      continue
    }

    markers.push({
      sourceBlockId,
      tableReference,
      start: match.index,
      end: match.index + match[0].length,
      markerIndex,
    })
    markerIndex += 1
  }

  return markers
}
